import Dexie, { type Table } from "dexie";
import { API_BASE, getToken } from "./api.js";

/**
 * Audio, fetched once and kept.
 *
 * Two problems, one answer.
 *
 * An <audio src="..."> element cannot carry an Authorization header, so on
 * Android — where the session is a bearer token rather than a cookie — every
 * uploaded track came back 401 and played silence. Fetching the bytes and
 * handing the element a blob URL fixes that, because the fetch can be
 * authenticated even though the element cannot.
 *
 * And having fetched the bytes, throwing them away would be careless. They go
 * into IndexedDB, so a ground with no signal still has the music and the
 * commentary that were there when you left the house.
 */

interface Blobbed {
  url: string;          // the request path, used as the key
  type: string;
  bytes: ArrayBuffer;
  at: number;
}

class Media extends Dexie {
  files!: Table<Blobbed, string>;
  constructor() {
    super("orbit-media");
    this.version(1).stores({ files: "url, at" });
  }
}

const db = new Media();
const objectUrls = new Map<string, string>();

/** Fetch with whatever authentication this platform uses. */
async function fetchAuthed(path: string): Promise<Response> {
  const token = getToken();
  return fetch(API_BASE + path, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
}

/**
 * A playable URL for a path, from disk if it is there.
 *
 * Returns null rather than throwing when a file is neither cached nor
 * reachable — the caller usually wants to carry on quietly rather than show an
 * error about a jingle.
 */
export async function mediaUrl(path: string): Promise<string | null> {
  const live = objectUrls.get(path);
  if (live) return live;

  try {
    const hit = await db.files.get(path);
    if (hit) {
      const url = URL.createObjectURL(new Blob([hit.bytes], { type: hit.type }));
      objectUrls.set(path, url);
      return url;
    }
  } catch { /* storage unavailable; fall through to the network */ }

  try {
    const r = await fetchAuthed(path);
    if (!r.ok) return null;
    const type = r.headers.get("content-type") ?? "audio/mpeg";
    const bytes = await r.arrayBuffer();
    try { await db.files.put({ url: path, type, bytes, at: Date.now() }); }
    catch { /* quota; still playable this session */ }
    const url = URL.createObjectURL(new Blob([bytes], { type }));
    objectUrls.set(path, url);
    return url;
  } catch {
    return null;
  }
}

/**
 * The raw bytes, for Web Audio.
 *
 * mediaUrl hands back a blob: URL, which an <audio> element plays happily —
 * media-src allows blob:. But decodeAudioData needs the bytes, and fetching a
 * blob: URL to get them goes through connect-src, which does not allow blob:.
 * The clips downloaded and then silently never played.
 *
 * Returning the buffer directly avoids the round trip entirely, and is quicker:
 * no blob, no second fetch, no object URL to revoke.
 */
/**
 * A clip that shipped inside the app, if there is one.
 *
 * The APK carries every commentary recording, so on Android the audio must not
 * depend on a network at all — a club ground is exactly where there isn't one.
 * The bundled copy lives at /commentary/<pack>/<file>, which is the API path
 * without its /api/v1 prefix.
 *
 * In a browser this 404s once per clip and falls straight through, so the same
 * code is correct in both places without asking which one it is running in.
 */
async function bundled(path: string): Promise<ArrayBuffer | null> {
  // Accept an absolute URL as well as a bare path. clipFor() builds URLs with
  // API_BASE in front while prime() passes bare paths, and a prefix check that
  // only understood one of them meant every clip the scorer actually played
  // missed the copy sitting on the device and was fetched over the network —
  // which is exactly the lag this bundling was meant to remove.
  const rel = path.startsWith("http") ? path.replace(/^https?:\/\/[^/]+/, "") : path;
  if (!rel.startsWith("/api/v1/commentary/")) return null;
  try {
    const r = await fetch(rel.replace("/api/v1/commentary/", "/commentary/"));
    if (!r.ok) return null;
    // Deliberately not cached in IndexedDB: it is already on the device, and
    // storing a second copy would spend the user's quota to no purpose.
    return await r.arrayBuffer();
  } catch {
    return null;
  }
}

export async function mediaBytes(path: string): Promise<ArrayBuffer | null> {
  try {
    const hit = await db.files.get(path);
    if (hit) return hit.bytes;
  } catch { /* storage unavailable */ }

  const inApp = await bundled(path);
  if (inApp) return inApp;

  try {
    const r = await fetchAuthed(path);
    if (!r.ok) return null;
    const type = r.headers.get("content-type") ?? "audio/mpeg";
    const bytes = await r.arrayBuffer();
    try { await db.files.put({ url: path, type, bytes, at: Date.now() }); }
    catch { /* quota */ }
    return bytes;
  } catch {
    return null;
  }
}

/** Is this file already on the device? */
export async function isCached(path: string): Promise<boolean> {
  try { return (await db.files.get(path)) !== undefined; } catch { return false; }
}

export interface Progress { done: number; total: number; failed: number }

/**
 * Pull a set of files down for offline use.
 *
 * Sequential on purpose: forty parallel requests on a phone tethered to a
 * hotspot is how you get a stalled queue and a confusing progress bar.
 */
export async function cacheAll(
  paths: string[],
  onProgress?: (p: Progress) => void,
): Promise<Progress> {
  let done = 0, failed = 0;
  for (const path of paths) {
    if (await isCached(path)) { done++; onProgress?.({ done, total: paths.length, failed }); continue; }
    const ok = await mediaUrl(path);
    if (ok) done++; else failed++;
    onProgress?.({ done, total: paths.length, failed });
  }
  return { done, total: paths.length, failed };
}

/** How much is on the device, for a settings screen to report. */
export async function cacheSize(): Promise<{ files: number; bytes: number }> {
  try {
    let bytes = 0, files = 0;
    await db.files.each((f) => { bytes += f.bytes.byteLength; files += 1; });
    return { files, bytes };
  } catch {
    return { files: 0, bytes: 0 };
  }
}

export async function clearCache(): Promise<void> {
  for (const url of objectUrls.values()) URL.revokeObjectURL(url);
  objectUrls.clear();
  try { await db.files.clear(); } catch { /* nothing to clear */ }
}

/* ── what "everything" means ────────────────────────────────────────────── */

/** Every commentary clip for a pack, read from its manifest. */
export async function commentaryPaths(pack: string): Promise<string[]> {
  const manifestPath = `/api/v1/commentary/${pack}/manifest.json`;
  try {
    const r = await fetchAuthed(manifestPath);
    if (!r.ok) return [];
    const bytes = await r.arrayBuffer();
    // Cache the manifest itself. Without it nothing knows which clip belongs to
    // which event, so 450 cached clips are unreachable and the app falls back
    // to the phone's voice — which is precisely how it went silent offline.
    try {
      await db.files.put({ url: manifestPath, type: "application/json", bytes, at: Date.now() });
    } catch { /* quota */ }
    const m = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, { file: string }[]>;
    return [manifestPath, ...Object.values(m).flat().map((c) => `/api/v1/commentary/${pack}/${c.file}`)];
  } catch {
    return [];
  }
}

/** The crowd bed and its reactions. */
export function crowdPaths(): string[] {
  return ["crowd-bed", "cheer-small", "cheer-big", "roar", "clap"]
    .map((n) => `/api/v1/commentary/v1/crowd/${n}.mp3`);
}

/** Every track the club has uploaded. */
export async function musicPaths(clubId: string): Promise<string[]> {
  try {
    const r = await fetchAuthed(`/api/v1/clubs/${clubId}/audio`);
    if (!r.ok) return [];
    const d = (await r.json()) as { tracks: { id: string }[] };
    return d.tracks.map((t) => `/api/v1/audio/${t.id}/stream`);
  } catch {
    return [];
  }
}


/* ── reference data ─────────────────────────────────────────────────────── */

/**
 * Warm the data caches too.
 *
 * Audio was only half the problem. The screens read through a cache, but a
 * cache is empty until a screen has been visited — so downloading every clip
 * and then walking to a ground still gave you an empty match list, because you
 * had never opened it while you had signal.
 *
 * This fetches the club's own data through the same cache the screens use, so
 * they paint from disk whether or not you happened to look at them first.
 */
export async function cacheClubData(clubId: string): Promise<number> {
  const { cacheSet } = await import("./outbox.js");
  const paths: [string, string][] = [
    [`club:${clubId}`,    `/api/v1/clubs/${clubId}`],
    [`matches:${clubId}`, `/api/v1/clubs/${clubId}/matches`],
    [`audio:${clubId}`,   `/api/v1/clubs/${clubId}/audio`],
    [`cups:${clubId}`,    `/api/v1/clubs/${clubId}/tournaments`],
  ];

  let saved = 0;
  for (const [key, path] of paths) {
    try {
      const r = await fetchAuthed(path);
      if (!r.ok) continue;
      await cacheSet(key, await r.json());
      saved += 1;
    } catch { /* offline already, or the endpoint is unavailable */ }
  }

  // And a snapshot of every match, so a scorer can open one at the ground.
  try {
    const r = await fetchAuthed(`/api/v1/clubs/${clubId}/matches`);
    if (r.ok) {
      const d = (await r.json()) as { matches: { matchId: string }[] };
      for (const m of d.matches.slice(0, 20)) {
        try {
          const s = await fetchAuthed(`/api/v1/matches/${m.matchId}/snapshot`);
          if (s.ok) { await cacheSet(`snap:${m.matchId}`, await s.json()); saved += 1; }
        } catch { /* skip this one */ }
      }
    }
  } catch { /* nothing to warm */ }

  return saved;
}
